const TIANDITU_KEY =
  process.env.VUE_APP_TIANDITU_KEY || "b43bfaadd4b9474e7b808c0dd7fb911c";


const PI = Math.PI;
const A = 6378245.0;
const EE = 0.00669342162296594323;

let loaderPromise;
let activeMap;

function outOfChina(lng, lat) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function transformLat(lng, lat) {
  let result =
    -100.0 +
    2.0 * lng +
    3.0 * lat +
    0.2 * lat * lat +
    0.1 * lng * lat +
    0.2 * Math.sqrt(Math.abs(lng));
  result +=
    ((20.0 * Math.sin(6.0 * lng * PI) +
      20.0 * Math.sin(2.0 * lng * PI)) *
      2.0) /
    3.0;
  result +=
    ((20.0 * Math.sin(lat * PI) +
      40.0 * Math.sin((lat / 3.0) * PI)) *
      2.0) /
    3.0;
  result +=
    ((160.0 * Math.sin((lat / 12.0) * PI) +
      320 * Math.sin((lat * PI) / 30.0)) *
      2.0) /
    3.0;
  return result;
}

function transformLng(lng, lat) {
  let result =
    300.0 +
    lng +
    2.0 * lat +
    0.1 * lng * lng +
    0.1 * lng * lat +
    0.1 * Math.sqrt(Math.abs(lng));
  result +=
    ((20.0 * Math.sin(6.0 * lng * PI) +
      20.0 * Math.sin(2.0 * lng * PI)) *
      2.0) /
    3.0;
  result +=
    ((20.0 * Math.sin(lng * PI) +
      40.0 * Math.sin((lng / 3.0) * PI)) *
      2.0) /
    3.0;
  result +=
    ((150.0 * Math.sin((lng / 12.0) * PI) +
      300.0 * Math.sin((lng / 30.0) * PI)) *
      2.0) /
    3.0;
  return result;
}

function wgs84ToGcj02(lng, lat) {
  lng = Number(lng);
  lat = Number(lat);
  if (outOfChina(lng, lat)) return [lng, lat];

  let dLat = transformLat(lng - 105.0, lat - 35.0);
  let dLng = transformLng(lng - 105.0, lat - 35.0);
  const radLat = (lat / 180.0) * PI;
  let magic = Math.sin(radLat);
  magic = 1 - EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat =
    (dLat * 180.0) /
    (((A * (1 - EE)) / (magic * sqrtMagic)) * PI);
  dLng =
    (dLng * 180.0) / ((A / sqrtMagic) * Math.cos(radLat) * PI);
  return [lng + dLng, lat + dLat];
}

function gcj02ToWgs84(lng, lat) {
  lng = Number(lng);
  lat = Number(lat);
  if (outOfChina(lng, lat)) return [lng, lat];
  const converted = wgs84ToGcj02(lng, lat);
  return [lng * 2 - converted[0], lat * 2 - converted[1]];
}

function normalizePoint(point) {
  if (Array.isArray(point)) return [Number(point[0]), Number(point[1])];
  return [Number(point.lng), Number(point.lat)];
}

function toTiandituPoint(point) {
  const legacyPoint = normalizePoint(point);
  const wgs84Point = gcj02ToWgs84(legacyPoint[0], legacyPoint[1]);
  return new window.T.LngLat(wgs84Point[0], wgs84Point[1]);
}

function toLegacyPoint(point) {
  const gcj02Point = wgs84ToGcj02(Number(point.lng), Number(point.lat));
  return { lng: gcj02Point[0], lat: gcj02Point[1] };
}

function prepareTianditu() {
  if (window.T && window.T.Protocol) {
    window.T.Protocol.value = "https://";
  }
}


function loadTianditu() {
  if (window.T && window.T.Map) {
    prepareTianditu();
    return Promise.resolve(window.T);
  }
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById("tianditu-jsapi");
    if (existingScript) {
      existingScript.addEventListener(
        "load",
        () => {
          prepareTianditu();
          resolve(window.T);
        },
        { once: true }
      );
      existingScript.addEventListener(
        "error",
        () => reject(new Error("天地图 JavaScript API 加载失败")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.id = "tianditu-jsapi";
    script.type = "text/javascript";
    script.async = true;
    script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${encodeURIComponent(
      TIANDITU_KEY
    )}`;
    script.onload = () => {
      if (window.T && window.T.Map) {
        prepareTianditu();
        resolve(window.T);
      } else {
        reject(new Error("天地图 JavaScript API 未正确初始化"));
      }
    };
    script.onerror = () => reject(new Error("天地图 JavaScript API 加载失败"));
    document.head.appendChild(script);
  });

  return loaderPromise;
}

class Pixel {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
}

class MapAdapter {
  constructor(container, options = {}) {
    prepareTianditu();
    this._map = new window.T.Map(container, { projection: "EPSG:900913" });
    this._map.centerAndZoom(
      toTiandituPoint(options.center || [121.232102, 28.135212]),
      options.zoom || 13
    );
    if (window.T.Control && window.T.Control.Scale) {
      this._map.addControl(new window.T.Control.Scale());
    }
    activeMap = this;
  }

  add(overlays) {
    const items = Array.isArray(overlays) ? overlays : [overlays];
    items.filter(Boolean).forEach((item) => {
      if (item._overlay) this._map.addOverLay(item._overlay);
      if (item._extraOverlays) {
        item._extraOverlays.forEach((overlay) => this._map.addOverLay(overlay));
      }
      item._map = this;
      if (item._enableDragging) item._enableDragging();
    });
  }

  remove(overlays) {
    const items = Array.isArray(overlays) ? overlays : [overlays];
    items.filter(Boolean).forEach((item) => {
      if (item._overlay) this._map.removeOverLay(item._overlay);
      if (item._extraOverlays) {
        item._extraOverlays.forEach((overlay) => this._map.removeOverLay(overlay));
      }
    });
  }

  setCenter(point) {
    const tPoint = toTiandituPoint(point);
    if (typeof this._map.panTo === "function") this._map.panTo(tPoint);
    else this._map.centerAndZoom(tPoint, this._map.getZoom());
  }

  on(eventName, callback) {
    this._map.addEventListener(eventName, (event) => {
      const normalizedEvent = { ...event };
      if (event.lnglat) normalizedEvent.lnglat = toLegacyPoint(event.lnglat);
      callback(normalizedEvent);
    });
  }
}

class MarkerAdapter {
  constructor(options = {}) {
    const tPoint = toTiandituPoint(options.position);
    this._overlay = new window.T.Marker(tPoint);
    this._extraOverlays = [];
    this._draggable = Boolean(options.draggable);

    if (options.title && this._overlay.setTitle) {
      this._overlay.setTitle(options.title);
    }
    if (options.label && options.label.content && window.T.Label) {
      const offset = options.offset || new Pixel(15, -20);
      this._extraOverlays.push(
        new window.T.Label({
          text: options.label.content,
          position: tPoint,
          offset: new window.T.Point(offset.x, offset.y),
        })
      );
    }
    if (options.map) options.map.add(this);
  }

  _enableDragging() {
    if (this._draggable && this._overlay.enableDragging) {
      this._overlay.enableDragging();
    }
  }

  on(eventName, callback) {
    this._overlay.addEventListener(eventName, (event) => {
      const normalizedEvent = { ...event };
      if (event.lnglat) normalizedEvent.lnglat = toLegacyPoint(event.lnglat);
      callback(normalizedEvent);
    });
  }

  getPosition() {
    return toLegacyPoint(this._overlay.getLngLat());
  }
}

class GeolocationAdapter {
  constructor(options = {}) {
    this.options = options;
  }

  getCurrentPosition(callback) {
    if (!navigator.geolocation) {
      callback("error", { message: "当前浏览器不支持定位" });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const legacyPoint = wgs84ToGcj02(
          position.coords.longitude,
          position.coords.latitude
        );
        callback("complete", {
          position: { lng: legacyPoint[0], lat: legacyPoint[1] },
          accuracy: position.coords.accuracy,
        });
      },
      (error) => callback("error", error),
      {
        enableHighAccuracy: this.options.enableHighAccuracy !== false,
        timeout: this.options.timeout || 10000,
      }
    );
  }
}

function normalizeGeocoderResult(result) {
  const status = result && result.getStatus ? Number(result.getStatus()) : 0;
  if (status !== 0) return null;

  const nestedResult =
    (result && result.result && result.result.result) ||
    (result && result.result) ||
    result ||
    {};
  const component =
    (result && result.getAddressComponent && result.getAddressComponent()) ||
    nestedResult.addressComponent ||
    nestedResult.address_component ||
    {};
  const formattedAddress =
    (result && result.getAddress && result.getAddress()) ||
    nestedResult.formatted_address ||
    nestedResult.formattedAddress ||
    nestedResult.address ||
    "";

  if (!formattedAddress) return null;
  return {
    regeocode: {
      formattedAddress,
      addressComponent: {
        province: component.province || "",
        city: component.city || "",
        district: component.county || component.district || "",
      },
    },
  };
}

class GeocoderAdapter {
  getAddress(point, callback) {
    const geocoder = new window.T.Geocoder();
    geocoder.getLocation(toTiandituPoint(point), (result) => {
      const normalizedResult = normalizeGeocoderResult(result);
      callback(
        normalizedResult ? "complete" : "error",
        normalizedResult || result
      );
    });
  }
}

function normalizePois(result) {
  const pois =
    (result && result.getPois && result.getPois()) ||
    (result && result.pois) ||
    [];
  return pois
    .map((poi) => {
      const lonlat = String(poi.lonlat || "").trim().split(/[ ,]+/);
      if (lonlat.length < 2) return null;
      const legacyPoint = wgs84ToGcj02(Number(lonlat[0]), Number(lonlat[1]));
      return {
        ...poi,
        location: { lng: legacyPoint[0], lat: legacyPoint[1] },
      };
    })
    .filter(Boolean);
}

class PlaceSearchAdapter {
  constructor(options = {}) {
    this.map = options.map || activeMap;
  }

  search(keyword, callback) {
    if (!this.map || !this.map._map) {
      callback("error", { poiList: { pois: null } });
      return;
    }
    const search = new window.T.LocalSearch(this.map._map, {
      pageCapacity: 10,
      onSearchComplete: (result) => {
        const pois = normalizePois(result);
        callback("complete", { poiList: { pois: pois.length ? pois : null } });
      },
    });
    search.search(keyword);
  }
}

class AutocompleteAdapter {
  constructor(options = {}) {
    this.listeners = {};
    this.input = document.getElementById(options.input);
    if (this.input && this.input.tagName !== "INPUT") {
      this.input = this.input.querySelector("input");
    }
    if (!this.input) return;

    this.dropdown = document.createElement("div");
    Object.assign(this.dropdown.style, {
      position: "fixed",
      zIndex: "10000",
      display: "none",
      overflow: "hidden",
      background: "#fff",
      border: "1px solid #d9d9d9",
      borderRadius: "4px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    });
    document.body.appendChild(this.dropdown);

    let timer;
    this.input.addEventListener("input", () => {
      clearTimeout(timer);
      const keyword = this.input.value.trim();
      if (!keyword) {
        this.hide();
        return;
      }
      timer = setTimeout(() => this.search(keyword), 300);
    });
    this.input.addEventListener("blur", () => {
      setTimeout(() => this.hide(), 150);
    });
  }

  addListener(eventName, callback) {
    this.listeners[eventName] = callback;
  }

  search(keyword) {
    new PlaceSearchAdapter({ map: activeMap }).search(keyword, (status, result) => {
      const pois = result.poiList.pois || [];
      if (status !== "complete" || !pois.length) {
        this.hide();
        return;
      }
      this.render(pois.slice(0, 8));
    });
  }

  render(pois) {
    const rect = this.input.getBoundingClientRect();
    Object.assign(this.dropdown.style, {
      display: "block",
      left: `${rect.left}px`,
      top: `${rect.bottom + 2}px`,
      width: `${rect.width}px`,
    });
    this.dropdown.innerHTML = "";
    pois.forEach((poi) => {
      const item = document.createElement("div");
      item.textContent = poi.address ? `${poi.name} - ${poi.address}` : poi.name;
      Object.assign(item.style, {
        padding: "8px 12px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      });
      item.addEventListener("mouseenter", () => {
        item.style.background = "#f5f5f5";
      });
      item.addEventListener("mouseleave", () => {
        item.style.background = "#fff";
      });
      item.addEventListener("mousedown", (event) => {
        event.preventDefault();
        this.input.value = poi.name;
        this.input.dispatchEvent(new Event("input", { bubbles: true }));
        this.hide();
        if (this.listeners.select) this.listeners.select({ poi });
      });
      this.dropdown.appendChild(item);
    });
  }

  hide() {
    if (this.dropdown) this.dropdown.style.display = "none";
  }
}

const Tianditu = {
  Map: MapAdapter,
  Marker: MarkerAdapter,
  Pixel,
  Geolocation: GeolocationAdapter,
  Geocoder: GeocoderAdapter,
  Autocomplete: AutocompleteAdapter,
  PlaceSearch: PlaceSearchAdapter,
  plugin(plugins, callback) {
    if (typeof plugins === "function") plugins();
    else if (callback) callback();
  },
  event: {
    addListener(target, eventName, callback) {
      if (target && target.addListener) target.addListener(eventName, callback);
    },
  },
};

export { gcj02ToWgs84, wgs84ToGcj02 };

export default {
  load() {
    return loadTianditu().then(() => Tianditu);
  },
};
